import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus, Building2, Users, Calendar, User, Info, FileText, ArrowLeft, ArrowRight, RefreshCw, Tag, Bell, Shield, Gift, ChevronDown } from 'lucide-react';
import { RegisterData } from '../types';
import { useAuth } from '../hooks/useAuth';
import AnimatedLink from '../components/AnimatedLink';
import CustomerNavbar from '../components/CustomerNavbar';
import EmailVerificationModal from '../components/EmailVerificationModal';
import Footer from '../components/Footer';
import LearnAboutAACoreModal from '../components/LearnAboutAACoreModal';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { formatPhoneNumber } from '../utils/phoneFormatUtils';
import { applyRegistrationIdentityFieldErrors, focusRegistrationField } from '../utils/registrationIdentityErrors';

type AccountType = 'business' | 'customer';

const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [emailAvailability, setEmailAvailability] = useState<{ available: boolean; checking: boolean; message?: string }>({ available: true, checking: false });
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isVerificationEmailKnownToExist, setIsVerificationEmailKnownToExist] = useState(false);
  const [isCheckingVerificationEmailExists, setIsCheckingVerificationEmailExists] = useState(false);
  const { logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect and type parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const typeParam = searchParams.get('type') as AccountType | null;

  // Auto-select account type from URL parameter
  useEffect(() => {
    if (typeParam && (typeParam === 'business' || typeParam === 'customer')) {
      setSelectedAccountType(typeParam);
    }
  }, [typeParam]);

  // Form visibility when account type is selected
  useEffect(() => {
    if (selectedAccountType) {
        setIsFormVisible(true);
    } else {
      setIsFormVisible(false);
    }
  }, [selectedAccountType]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<RegisterData & { confirmPassword: string; acceptAgreement: boolean }>();

  const password = watch('password');
  const email = watch('email');

  const handleOpenVerificationModal = () => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    setVerificationEmail(normalizedEmail && normalizedEmail.includes('@') ? normalizedEmail : '');
    setShowVerificationModal(true);
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationEmail('');
    setIsVerificationEmailKnownToExist(false);
    setIsCheckingVerificationEmailExists(false);
  };

  // When the verification modal is open and the user edits the email there, check if it exists.
  // Only show the "Email verification required..." copy when we know the email exists.
  useEffect(() => {
    if (!showVerificationModal) return;

    const normalizedEmail = verificationEmail.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setIsVerificationEmailKnownToExist(false);
      setIsCheckingVerificationEmailExists(false);
      return;
    }

    setIsCheckingVerificationEmailExists(true);
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const result = await apiService.checkEmailExists(normalizedEmail);
        if (cancelled) return;
        setIsVerificationEmailKnownToExist(!!result.exists);
      } catch {
        if (cancelled) return;
        setIsVerificationEmailKnownToExist(false);
      } finally {
        if (cancelled) return;
        setIsCheckingVerificationEmailExists(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [showVerificationModal, verificationEmail]);

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      const normalizedEmail = verificationEmail.trim().toLowerCase();
      if (!normalizedEmail || !normalizedEmail.includes('@')) {
        toast.error('Please enter a valid email address.');
        return;
      }
      const result = await apiService.resendVerification(verificationEmail);
      if (result.success) {
        toast.success('Verification email sent successfully! Please check your inbox.');
        setShowVerificationModal(false);
        navigate(`/account-verification-sent?email=${encodeURIComponent(normalizedEmail)}&type=customer`);
      } else {
        toast.error(result.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to send verification email';
      toast.error(errorMsg);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const onSubmit = async (data: RegisterData & { confirmPassword: string; acceptAgreement: boolean }) => {
    if (!selectedAccountType) {
      toast.error('Please select an account type');
      return;
    }

    // Check if email is available before submitting
    if (!emailAvailability.available) {
      toast.error('This email is already registered. Please use a different email address.');
      return;
    }

    if (!data.acceptAgreement) {
      toast.error('Please review and agree to the account agreement to continue.');
      return;
    }

    setIsLoading(true);
    try {
      // Remove confirmPassword from data before sending to API
      const { confirmPassword, acceptAgreement, ...registerData } = data;
      
      // Convert birthday fields to numbers or undefined
      // HTML select elements return strings, so we need to handle both string and number types
      const birthMonthValue = registerData.birthMonth;
      if (birthMonthValue !== undefined && birthMonthValue !== null && birthMonthValue !== '') {
        registerData.birthMonth = typeof birthMonthValue === 'string' 
          ? parseInt(birthMonthValue, 10) 
          : birthMonthValue;
      } else {
        registerData.birthMonth = undefined;
      }
      
      const birthDayValue = registerData.birthDay;
      if (birthDayValue !== undefined && birthDayValue !== null && birthDayValue !== '') {
        registerData.birthDay = typeof birthDayValue === 'string' 
          ? parseInt(birthDayValue, 10) 
          : birthDayValue;
      } else {
        registerData.birthDay = undefined;
      }
      
      // Call the actual registration API
      await apiService.register(registerData);
      
      toast.success(`Account created successfully! Welcome to ${selectedAccountType === 'business' ? 'Business' : 'Customer'} registration.`);
      
      // Redirect to success page with account type
      setTimeout(() => {
        const successUrl = redirectUrl 
          ? `/registration-success?type=${selectedAccountType}&redirect=${encodeURIComponent(redirectUrl)}`
          : `/registration-success?type=${selectedAccountType}`;
        navigate(successUrl);
      }, 1000);
    } catch (error: any) {
      console.error('Registration error:', error);
      const identityFields = applyRegistrationIdentityFieldErrors(error, (field, message) => {
        if (field === 'businessEmail') return;
        setError(field, { type: 'server', message });
      });
      if (identityFields.length > 0) {
        const first = identityFields[0];
        focusRegistrationField(first);
        return;
      }
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountTypeSelect = (type: AccountType) => {
    setSelectedAccountType(type);
  };


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const checkEmailAvailability = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailAvailability({ available: true, checking: false });
      return;
    }

    setEmailAvailability({ available: false, checking: true });
    
    try {
      const result = await apiService.checkEmailExists(email);
      setEmailAvailability({ 
        available: !result.exists, 
        checking: false, 
        message: result.exists ? 'This email is already registered' : 'Email is available'
      });
    } catch (error) {
      console.error('Error checking email availability:', error);
      setEmailAvailability({ 
        available: true, 
        checking: false, 
        message: 'Unable to verify email availability. Please try again.' 
      });
    }
  };

  // Debounced email check
  const debouncedEmailCheck = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      return (email: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          checkEmailAvailability(email);
        }, 500);
      };
    }, []),
    []
  );

  // Watch email field and trigger availability check
  useEffect(() => {
    if (email && email.includes('@')) {
      debouncedEmailCheck(email);
    } else if (!email) {
      setEmailAvailability({ available: true, checking: false });
    }
  }, [email, debouncedEmailCheck]);

  // Google Sign-In handler
  const handleGoogleSignIn = useCallback(async (response: any) => {
    setIsLoading(true);
    try {
      // Extract the credential string from the response
      let credential: string;
      
      if (typeof response === 'string') {
        credential = response;
      } else if (response?.credential) {
        credential = typeof response.credential === 'string' 
          ? response.credential 
          : response.credential.credential;
      } else {
        throw new Error('No credential received from Google');
      }
      
      if (!credential || typeof credential !== 'string') {
        throw new Error('Invalid credential format from Google');
      }

      const authResponse = await apiService.googleLogin(credential, 'customer');
      
      // Store token and user
      localStorage.setItem('authToken', authResponse.token);
      localStorage.setItem('user', JSON.stringify(authResponse.user));
      localStorage.setItem('loginType', 'customer');
      
      // Update auth context user state
      updateUser(authResponse.user);
      
      toast.success('Successfully signed in with Google!');
      const finalRedirectUrl = redirectUrl || '/dashboard/customer';
      setTimeout(() => {
        navigate(finalRedirectUrl);
      }, 500);
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      let errorMsg: string;
      if (!error.response && (error.request || error.message?.toLowerCase().includes('network'))) {
        errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMsg = error.response?.data?.message || error.message || 'Google sign-in failed. Please try again.';
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, redirectUrl, updateUser]);

  // Initialize Google Sign-In when component mounts
  const googleSignInRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (selectedAccountType === 'customer' && (window as any).google && googleSignInRef.current) {
        (window as any).google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '946690405620-31eg6dhvpvf2mud0t67dtdmo6bjbkpif.apps.googleusercontent.com',
          callback: handleGoogleSignIn,
        });

        (window as any).google.accounts.id.renderButton(
          googleSignInRef.current,
          {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            width: '100%'
          }
        );
      }
    };

    // Wait for Google script to load
    if ((window as any).google) {
      initializeGoogleSignIn();
    } else {
      // Check periodically if Google script has loaded
      const checkInterval = setInterval(() => {
        if ((window as any).google) {
          initializeGoogleSignIn();
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }

    return () => {
      // Cleanup function
    };
  }, [selectedAccountType, handleGoogleSignIn]);

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={true}
      />
      
      {/* Main Content */}
      <div className="flex-1 min-w-0 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto">
          {/* Breadcrumb Navigation */}
          {selectedAccountType && (
            <div className="mb-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
            </div>
          )}

          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl font-bold mb-0">Create your account</h1>
            <p className="text-gray-600 text-sm mb-0">
              {selectedAccountType 
                ? 'Join us and start booking appointments'
                : 'Choose your account type and get started'
              }
            </p>
          </div>

          <div className="space-y-8">

        {/* Account Type Selection */}
        {!selectedAccountType && (
          <div className="grid md:grid-cols-2 gap-6 w-full">
            {/* Business/Freelancer Card */}
            <div 
              className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm hover:shadow-md flex flex-col"
            >
              <div className="flex-1 flex flex-col">
                {/* Header with icon and title */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-16 w-16 flex items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <Building2 className="h-7 w-7 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Business Owner / Freelancer
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Create your business profile, manage services, and start accepting appointments from customers.
                </p>
                <div className="space-y-3 text-sm text-gray-600 flex-1">
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Manage your business services</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Accept customer bookings</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Track appointments and revenue</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Professional booking website</span>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const businessRegUrl = redirectUrl 
                      ? `/business-registration/?redirect=${encodeURIComponent(redirectUrl)}`
                      : '/business-registration/';
                    navigate(businessRegUrl);
                  }}
                  className="aacore-btn-primary w-full md:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                  <Building2 className="h-4 w-4" />
                  Get Started
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsLearnMoreModalOpen(true);
                  }}
                  className="aacore-btn-outlined w-full md:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Info className="h-5 w-5" />
                  <span className="md:hidden">Learn More</span>
                  <span className="hidden md:inline">Learn about AACore Booking</span>
                </button>
                </div>
              </div>
            </div>

            {/* Customer Card */}
            <div 
              className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm hover:shadow-md flex flex-col"
            >
              <div className="flex-1 flex flex-col">
                {/* Header with icon and title */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-16 w-16 flex items-center justify-center rounded-lg bg-green-50 flex-shrink-0">
                    <User className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Customer
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Discover local businesses, book appointments, and manage your upcoming services.
                </p>
                <div className="space-y-3 text-sm text-gray-600 flex-1">
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Browse local businesses</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Book appointments easily</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Manage your bookings</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span>Leave reviews and ratings</span>
                  </div>
                </div>
                <button 
                  onClick={() => handleAccountTypeSelect('customer')}
                  className="aacore-btn-success mt-6 w-full md:w-auto md:self-start justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <UserPlus className="h-4 w-4" />
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Login Link for Account Type Selection */}
        {!selectedAccountType && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <AnimatedLink
                to="/login"
                className="text-blue-900 hover:text-blue-800"
              >
                Sign in here
              </AnimatedLink>
            </p>
          </div>
        )}

        {/* Registration Form */}
        {selectedAccountType && (
          <div className="w-full">
            <div className={selectedAccountType === 'customer' ? 'md:flex md:gap-6 md:items-stretch' : ''}>
              <div className={selectedAccountType === 'customer' ? 'md:w-1/2 md:flex md:flex-col' : 'w-full md:w-1/2'}>
                <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm flex flex-col flex-1">
                  <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                {/* First Name Field */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <div className="relative">
                    <input
                      id="firstName"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${errors.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter your first name"
                      {...register('firstName', {
                        required: 'First name is required',
                        minLength: {
                          value: 2,
                          message: 'First name must be at least 2 characters',
                        },
                      })}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                {/* Last Name Field */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <div className="relative">
                    <input
                      id="lastName"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${errors.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter your last name"
                      {...register('lastName', {
                        required: 'Last name is required',
                        minLength: {
                          value: 2,
                          message: 'Last name must be at least 2 characters',
                        },
                      })}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email address *
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${
                        errors.email || (!emailAvailability.available && !emailAvailability.checking) 
                          ? 'border-red-500 focus:ring-red-500' 
                          : ''
                      }`}
                      placeholder="Enter your email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                        onChange: (e) => {
                          const value = e.target.value.toLowerCase();
                          e.target.value = value;
                          setValue('email', value, { shouldValidate: false });
                          debouncedEmailCheck(value);
                        },
                      })}
                    />
                    {emailAvailability.checking && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      </div>
                    )}
                    {!emailAvailability.checking && !emailAvailability.available && emailAvailability.message && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                  {!emailAvailability.checking && !emailAvailability.available && (
                    <p className="mt-1 text-sm text-red-600">
                      {emailAvailability.message || 'This email is already registered. Please use a different email.'}
                    </p>
                  )}
                </div>

                {/* Phone number: required for customer accounts */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone number
                    {selectedAccountType === 'customer' ? ' *' : ' (optional)'}
                  </label>
                  <div className="relative">
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${errors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="(555) 123-4567"
                      aria-required={selectedAccountType === 'customer'}
                      {...register('phone', {
                        validate: (value) => {
                          const v = (value || '').trim();
                          if (selectedAccountType === 'customer') {
                            if (!v) return 'Phone number is required';
                            if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(v)) {
                              return 'Please enter a valid phone number in format (123) 456-7890';
                            }
                          } else if (v && !/^\(\d{3}\) \d{3}-\d{4}$/.test(v)) {
                            return 'Please enter a valid phone number in format (123) 456-7890';
                          }
                          return true;
                        },
                        onChange: (e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          e.target.value = formatted;
                          setValue('phone', formatted, { shouldValidate: false });
                        },
                      })}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message as string}</p>
                  )}
                </div>

                {/* Birthday Field (optional) */}
                {selectedAccountType === 'customer' && (
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="birthMonth" className="block text-sm font-medium text-gray-700">
                        Birthday <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(11, 71, 120, 0.1)', color: '#0B4778' }}>
                        <Gift className="h-3 w-3" />
                        Month & day only (no year)
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">Many businesses offer special birthday discounts. Share your month and day if you'd like.</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="birthMonth" className="block text-sm font-medium text-gray-700 mb-2">
                          Birth month
                        </label>
                        <div className="relative">
                          <select
                            id="birthMonth"
                            className={`aacore-textfield-primary appearance-none pr-10 ${errors.birthMonth ? 'border-red-500 focus:ring-red-500' : ''}`}
                            {...register('birthMonth', {
                              validate: (value) => {
                                const day = watch('birthDay');
                                const monthValue = typeof value === 'string' ? parseInt(value, 10) : value;
                                const dayValue = typeof day === 'string' ? parseInt(day, 10) : day;
                                
                                if (dayValue && !monthValue) {
                                  return 'Please select a birth month if you select a birth day';
                                }
                                if (monthValue && (monthValue < 1 || monthValue > 12)) {
                                  return 'Please select a valid month';
                                }
                                return true;
                              }
                            })}
                          >
                            <option value="">Select month</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                              const date = new Date(2000, month - 1, 1);
                              const monthName = date.toLocaleString('default', { month: 'long' });
                              return (
                                <option key={month} value={month}>
                                  {monthName}
                                </option>
                              );
                            })}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                        {errors.birthMonth && (
                          <p className="mt-1 text-sm text-red-600">{errors.birthMonth.message as string}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="birthDay" className="block text-sm font-medium text-gray-700 mb-2">
                          Birth day
                        </label>
                        <div className="relative">
                          <select
                            id="birthDay"
                            className={`aacore-textfield-primary appearance-none pr-10 ${errors.birthDay ? 'border-red-500 focus:ring-red-500' : ''}`}
                            {...register('birthDay', {
                              validate: (value) => {
                                const month = watch('birthMonth');
                                const dayValue = typeof value === 'string' ? parseInt(value, 10) : value;
                                const monthValue = typeof month === 'string' ? parseInt(month, 10) : month;
                                
                                if (dayValue && !monthValue) {
                                  return 'Please select a birth month if you select a birth day';
                                }
                                if (dayValue && (dayValue < 1 || dayValue > 31)) {
                                  return 'Please select a valid day';
                                }
                                // Validate day is valid for the selected month
                                if (dayValue && monthValue) {
                                  const daysInMonth = new Date(2000, monthValue, 0).getDate();
                                  if (dayValue > daysInMonth) {
                                    return `Selected month only has ${daysInMonth} days`;
                                  }
                                }
                                return true;
                              }
                            })}
                          >
                            <option value="">Select day</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                        {errors.birthDay && (
                          <p className="mt-1 text-sm text-red-600">{errors.birthDay.message as string}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Create a password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                        },
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary pr-10 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Confirm your password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === password || 'Passwords do not match',
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Account Agreement */}
                <div className="md:col-span-2">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                      {...register('acceptAgreement', {
                        validate: (v) => v === true || 'You must agree before continuing',
                      })}
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <a
                        href={selectedAccountType === 'business' ? '/agreements/business' : '/agreements/customer'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-900 hover:underline"
                      >
                        {selectedAccountType === 'business'
                          ? 'AACore Business Account Agreement'
                          : 'AACore Customer User Agreement (Terms of Service)'}
                      </a>
                      <span className="text-red-500"> *</span>
                    </span>
                  </label>
                  {errors.acceptAgreement && (
                    <p className="mt-1 text-sm text-red-600">{errors.acceptAgreement.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="md:col-span-2 flex justify-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full md:w-auto justify-center aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating account...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        {selectedAccountType === 'business' ? (
                          <Building2 className="w-4 h-4 mr-2" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        Create {selectedAccountType === 'business' ? 'Business' : 'Customer'} Account
                      </div>
                    )}
                  </button>
                </div>
              </form>

              {/* Login Link (business only; customer uses the info card below) */}
              {selectedAccountType !== 'customer' && (
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <AnimatedLink
                      to="/login"
                      className="font-medium text-blue-900 hover:text-blue-800"
                    >
                      Sign in here
                    </AnimatedLink>
                  </p>
                </div>
              )}
                </div>
              </div>

              {/* Customer Info Card (right side on desktop) */}
              {selectedAccountType === 'customer' && (
                <div className="md:w-1/2 mt-4 md:mt-0 md:flex md:flex-col space-y-6">
                  {/* Helpful links section */}
                  <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                    <div className="flex gap-3 items-start">
                      <div className="hidden md:flex flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Info className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <strong className="text-gray-900 block mb-3">Helpful links</strong>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Already have an account?{' '}
                              <AnimatedLink to="/login?type=customer" className="font-medium text-blue-900 hover:text-blue-800">
                                Sign in here
                              </AnimatedLink>
                              .
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Have an account but not yet verified?{' '}
                              <button
                                type="button"
                                onClick={handleOpenVerificationModal}
                                className="font-medium text-blue-900 hover:text-blue-800"
                              >
                                Complete verification here
                              </button>
                              .
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Forgot your password?{' '}
                              <AnimatedLink to="/forgot-password?type=customer" className="font-medium text-blue-900 hover:text-blue-800">
                                Reset it
                              </AnimatedLink>
                              .
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Why create an account section */}
                  <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900">Why create an account?</h3>
                      <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(11, 71, 120, 0.1)', color: '#0B4778' }}>
                        <Tag className="h-3 w-3" />
                        Benefits
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">It takes less than a minute and makes booking much easier.</p>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)' }}>
                          <Calendar className="h-5 w-5" style={{ color: '#0B4778' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Manage appointments anytime</p>
                          <p className="text-sm text-gray-600">View upcoming visits, reschedule, or cancel (if allowed) in a few taps.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)' }}>
                          <RefreshCw className="h-5 w-5" style={{ color: '#0B4778' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Quick rebook</p>
                          <p className="text-sm text-gray-600">Rebook your favorite services and professionals without re-entering details.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)' }}>
                          <Tag className="h-5 w-5" style={{ color: '#0B4778' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Exclusive discounts & birthday offers</p>
                          <p className="text-sm text-gray-600">Opt-in birthday month/day helps businesses recognize you with special promotions.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)' }}>
                          <Bell className="h-5 w-5" style={{ color: '#0B4778' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Helpful reminders</p>
                          <p className="text-sm text-gray-600">Get confirmations and reminders so you never miss an appointment.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Your privacy matters section */}
                  <div className="bg-gray-50 border border-gray-200 p-5 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 border-gray-300 bg-white flex items-center justify-center">
                        <Shield className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm text-gray-900 mb-1">Your privacy matters</h3>
                        <p className="text-sm text-gray-600">Your information will never be sold to any third party. It's used only to manage your appointments and support your experience.</p>
                      </div>
                    </div>
                  </div>

                  {/* Skip this registration section */}
                  <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                    <h3 className="font-semibold text-sm text-gray-900 mb-1">Skip this registration</h3>
                    <p className="text-sm text-gray-600 mb-4">Sign in with your Google account instead.</p>
                    <div ref={googleSignInRef} className="w-full flex justify-center"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Learn About Modal */}
      <LearnAboutAACoreModal 
        isOpen={isLearnMoreModalOpen} 
        onClose={() => setIsLearnMoreModalOpen(false)} 
      />

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={handleCloseVerificationModal}
        onResendVerification={handleResendVerification}
        email={verificationEmail}
        onEmailChange={setVerificationEmail}
        showEmailInput
        isEmailKnownToExist={!isCheckingVerificationEmailExists && isVerificationEmailKnownToExist}
        isLoading={isResendingVerification}
      />
    </div>
  );
};

export default RegisterPage; 